import { useState, useEffect } from 'react';
import { useParams } from 'wouter';
import { useLocation, useRoute } from 'wouter';
import Header from '@/components/header';
import Footer from '@/components/Footer1';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Star, MapPin, Clock, Award, Users, Share, Heart, MessageCircle, ArrowLeft } from 'lucide-react';
import { MediaOwnerHome } from '@/components/media-owner/media-owner-home';
import { MediaOwnerInventory } from '@/components/media-owner/media-owner-inventory';
import { MediaOwnerDeals } from '@/components/media-owner/media-owner-deals';
import { MediaOwnerFeedback } from '@/components/media-owner/media-owner-feedback';
import { getMediaOwner } from '@/data/media-owners';
import logger from '@/utils/logger';

interface MediaOwner {
  id: string;
  name: string;
  logo: string;
  bannerImage: string;
  tagline: string;
  rating: number;
  reviewCount: number;
  followers: number;
  itemsSold: number;
  joinedDate: string;
  responseTime: string;
  location: string;
  description: string;
  verified: boolean;
  brandColor: string;
  specialties: string[];
}

// Default fallback media owner
const defaultMediaOwner: MediaOwner = {
  id: "ooh-unmissable",
  name: "OOH Unmissable Shop",
  logo: "/api/placeholder/120/120",
  bannerImage: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1200&h=400&fit=crop",
  tagline: "Premium Digital & Classic Billboard Solutions",
  rating: 4.9,
  reviewCount: 1984,
  followers: 8600,
  itemsSold: 2547,
  joinedDate: "Jun 2020",
  responseTime: "Usually responds within 24 hours",
  location: "Sydney, Australia",
  description: "We are Australia's leading outdoor advertising solutions provider, specializing in premium digital and classic billboard placements across major metropolitan areas.",
  verified: true,
  brandColor: "#059669",
  specialties: ["Premium Digital", "Classic Billboards", "Metropolitan Coverage"]
};

export default function MediaOwnerStore() {
  const params = useParams();
  const ownerId = params.ownerId;
  const location = useLocation();
  const [, navigate] = useRoute('/store/:ownerId');
  const [activeTab, setActiveTab] = useState('home');
  const [mediaOwner, setMediaOwner] = useState<MediaOwner | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);

  useEffect(() => {
    // Extract tab from URL hash or query params
    const searchParams = new URLSearchParams(window.location.search);
    const tabFromUrl = searchParams.get('tab') || 'home';
    setActiveTab(tabFromUrl);
    
    // Debug logging
    logger.log('MediaOwnerStore Debug:', {
      ownerId,
      ownerFromData: getMediaOwner(ownerId || '')
    });
    
    // Load media owner data based on ownerId
    const owner = getMediaOwner(ownerId || '');
    if (owner) {
      setMediaOwner(owner);
    } else {
      logger.warn(`Media owner not found for ID: ${ownerId}, using default`);
      setMediaOwner(defaultMediaOwner);
    }
  }, [location, ownerId]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    // Update URL without full page reload
    const newUrl = `${window.location.pathname}?tab=${tab}`;
    window.history.pushState(null, '', newUrl);
  };

  if (!mediaOwner) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Loading...</h2>
            <p className="text-gray-600">Please wait while we load the store.</p>
          </div>
        </div>
        <Footer configData={[]} yourLogo={""} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Button */}
        <div className="mb-4">
          <Button 
            variant="ghost" 
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-2"
            onClick={() => {
              // Navigate back to explore page
              window.location.href = '/explore';
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Store Header */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          {/* Banner Image */}
          <div className="relative h-48 sm:h-64 lg:h-80 bg-gray-200">
            <img
              src={mediaOwner.bannerImage}
              alt={`${mediaOwner.name} banner`}
              className="w-full h-full object-cover"
              onError={(e) => {
                logger.error(`Failed to load banner image for ${mediaOwner.name}:`, mediaOwner.bannerImage);
                // Set a fallback background color if image fails
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
              onLoad={() => {
                logger.log(`Banner image loaded successfully for ${mediaOwner.name}`);
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-20"></div>
          </div>
          
          {/* Store Info */}
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
              {/* Logo */}
              <div className="relative">
                <img
                  src={mediaOwner.logo}
                  alt={mediaOwner.name}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-white shadow-md object-cover"
                />
                {mediaOwner.verified && (
                  <div className="absolute -bottom-1 -right-1 bg-emerald-500 rounded-full p-1">
                    <Award className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>
              
              {/* Store Details */}
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mb-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{mediaOwner.name}</h1>
                  {mediaOwner.verified && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                      Verified
                    </Badge>
                  )}
                </div>
                
                <p className="text-gray-600 mb-3">{mediaOwner.tagline}</p>
                
                {/* Stats Row */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-400 fill-current" />
                    <span className="font-medium text-gray-900">{mediaOwner.rating}</span>
                    <span>({mediaOwner.reviewCount.toLocaleString()} reviews)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{mediaOwner.followers.toLocaleString()} followers</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>{mediaOwner.location}</span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button 
                  variant={isFollowing ? "secondary" : "default"}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setIsFollowing(!isFollowing)}
                >
                  <Heart className={`h-4 w-4 mr-2 ${isFollowing ? 'fill-current' : ''}`} />
                  {isFollowing ? 'Following' : 'Follow'}
                </Button>
                <Button variant="outline">
                  <Share className="h-4 w-4 mr-2" />
                  Share
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.location.href = `/contact/${ownerId}`}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Contact
                </Button>
              </div>
            </div>
            
            {/* Additional Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pt-4 border-t">
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{mediaOwner.responseTime}</span>
              </div>
              <span>•</span>
              <span>Joined {mediaOwner.joinedDate}</span>
              <span>•</span>
              <span>{mediaOwner.itemsSold.toLocaleString()} items sold</span>
            </div>
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-white rounded-lg shadow-sm p-1 mb-6">
            <TabsTrigger value="home" className="text-sm font-medium">Home</TabsTrigger>
            <TabsTrigger value="inventory" className="text-sm font-medium">Inventory</TabsTrigger>
            <TabsTrigger value="deals" className="text-sm font-medium">Deals</TabsTrigger>
            <TabsTrigger value="feedback" className="text-sm font-medium">Feedback</TabsTrigger>
          </TabsList>
          
          <TabsContent value="home">
            <MediaOwnerHome mediaOwner={mediaOwner} />
          </TabsContent>
          
          <TabsContent value="inventory">
            <MediaOwnerInventory ownerId={mediaOwner.id} />
          </TabsContent>
          
          <TabsContent value="deals">
            <MediaOwnerDeals ownerId={mediaOwner.id} />
          </TabsContent>
          
          <TabsContent value="feedback">
            <MediaOwnerFeedback mediaOwner={mediaOwner} />
          </TabsContent>
        </Tabs>
      </main>
      
      <Footer configData={[]} yourLogo={""} />
    </div>
  );
}